import api from './axiosBase';

export interface OutputSMSRequest {
  message: string;
  devMode: boolean;
  scheduledAt?: string; // ISO 8601
  timeZone?: string; // IANA timezone (e.g., "America/New_York")
}

export interface OutputEmailRequest {
  message: string;
  subject?: string;
  templateId?: string;
  devMode: boolean;
  scheduledAt?: string;
  timeZone?: string; // IANA timezone (e.g., "America/New_York")
}

export interface MakeEmailRequest {
  idea: string;
  templateId: string;
  versionId?: string;
  scheduledAt?: string;
  timeZone?: string;
  devMode?: boolean;
  messageIndex?: number; // Optional - if provided, update existing message instead of creating new
}

export interface MakeSMSRequest {
  idea: string;
}

export interface MakeSMSResponse {
  message: string;
  success: boolean;
}

export interface MakeSimpleEmailTextRequest {
  idea: string;
}

export interface MakeSimpleEmailTextResponse {
  message: string;
  subject: string;
  success: boolean;
}

export interface MakeEmailResponse {
  message: any; // The actual Message object with content, explainedMessage, etc.
  scheduled: boolean;
  status: string;
}

export interface OutputMessageResponse {
  success: boolean;
  message: string;
  messageId?: string;
  devMode: boolean;
}

/**
 * Send an SMS message to a user
 */
export async function sendSMS(
  projectID: string,
  simpleID: string,
  request: OutputSMSRequest
): Promise<OutputMessageResponse> {
  const response = await api.post<OutputMessageResponse>(
    `/v2/project/${projectID}/user/${simpleID}/output-sms`,
    request
  );

  return response.data;
}

/**
 * Send an email message to a user
 */
export async function sendEmail(
  projectID: string,
  simpleID: string,
  request: OutputEmailRequest
): Promise<OutputMessageResponse> {
  const response = await api.post<OutputMessageResponse>(
    `/v2/project/${projectID}/user/${simpleID}/output-email`,
    request
  );

  return response.data;
}

/**
 * Create an AI-generated email based on an idea and template
 */
export async function makeEmail(
  projectID: string,
  simpleID: string,
  request: MakeEmailRequest
): Promise<MakeEmailResponse> {
  try {
    const response = await api.post<MakeEmailResponse>(
      `/v2/project/${projectID}/user/${simpleID}/make-email`,
      request
    );

    return response.data;
  } catch (error: any) {
    // Check for document size limit error
    if (
      error.response?.status === 400 &&
      error.response?.data?.error?.includes('exceeds the maximum allowed size')
    ) {
      throw new Error(
        'Email too large to save. Try a simpler email idea or delete some old emails.'
      );
    }

    // Re-throw other errors with their original message
    throw new Error(
      error.response?.data?.error || error.message || 'Failed to generate email'
    );
  }
}

/**
 * Generate an AI SMS message based on an idea
 */
export async function makeSMS(
  projectID: string,
  simpleID: string,
  request: MakeSMSRequest
): Promise<MakeSMSResponse> {
  try {
    const response = await api.post<MakeSMSResponse>(
      `/v2/project/${projectID}/user/${simpleID}/make-sms`,
      request
    );

    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.error || error.message || 'Failed to generate SMS'
    );
  }
}

/**
 * Generate simple email text using AI based on an idea
 * This is for plain text emails without templates
 */
export async function makeSimpleEmailText(
  projectID: string,
  simpleID: string,
  request: MakeSimpleEmailTextRequest
): Promise<MakeSimpleEmailTextResponse> {
  try {
    const response = await api.post<MakeSimpleEmailTextResponse>(
      `/v2/project/${projectID}/user/${simpleID}/make-simple-email-text`,
      request
    );

    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.error ||
        error.message ||
        'Failed to generate email text'
    );
  }
}

/**
 * Get the compiled HTML for an email message
 * This fetches the HTML either from Firebase Storage (new messages) or compiles it on-the-fly (legacy messages)
 */
export async function getMessageHTML(
  projectID: string,
  simpleID: string,
  messageIndex: number
): Promise<string> {
  try {
    const response = await api.get<string>(
      `/v2/project/${projectID}/user/${simpleID}/messages/${messageIndex}/html`,
      {
        responseType: 'text'
      }
    );

    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.error ||
        error.message ||
        'Failed to fetch email HTML'
    );
  }
}

/**
 * Update a message's idea/content text
 */
export async function updateMessageIdea(
  projectID: string,
  simpleID: string,
  messageIndex: number,
  content: string
): Promise<void> {
  try {
    await api.patch(
      `/v2/project/${projectID}/user/${simpleID}/messages/${messageIndex}/idea`,
      { content }
    );
  } catch (error: any) {
    throw new Error(
      error.response?.data?.error ||
        error.message ||
        'Failed to update message idea'
    );
  }
}

/**
 * Update a text email's subject and content
 */
export async function updateTextEmail(
  projectID: string,
  simpleID: string,
  messageIndex: number,
  subject: string,
  content: string
): Promise<void> {
  try {
    await api.patch(
      `/v2/project/${projectID}/user/${simpleID}/messages/${messageIndex}/text-email`,
      { subject, content }
    );
  } catch (error: any) {
    throw new Error(
      error.response?.data?.error ||
        error.message ||
        'Failed to update text email'
    );
  }
}

// ========== Message Refresh Endpoints ==========

export interface NewActionInfo {
  timestamp: string;
  actionType: string;
  description: string;
}

export interface RefreshMessagePreviewResponse {
  messageId: string;
  messageIndex: number;
  currentIdea: string;
  lastRefreshedAt: string | null;
  newActionsCount: number;
  newActions: NewActionInfo[];
  canRefresh: boolean;
  refreshNotNeeded: boolean;
  autoRefreshNote: string;
}

export interface RefreshMessageResponse {
  success: boolean;
  messageId: string;
  originalIdea: string;
  updatedIdea: string;
  wasChanged: boolean;
  refreshedAt: string;
  actionsUsed: number;
}

/**
 * Get preview of what would be refreshed for a scheduled message
 * Shows new customer actions that haven't been considered yet
 */
export async function getRefreshMessagePreview(
  projectID: string,
  simpleID: string,
  messageIndex: number
): Promise<RefreshMessagePreviewResponse> {
  const response = await api.get<RefreshMessagePreviewResponse>(
    `/v2/project/${projectID}/user/${simpleID}/messages/${messageIndex}/refresh-preview`
  );
  return response.data;
}

/**
 * Refresh a scheduled message idea based on new customer actions
 */
export async function refreshMessage(
  projectID: string,
  simpleID: string,
  messageIndex: number
): Promise<RefreshMessageResponse> {
  const response = await api.post<RefreshMessageResponse>(
    `/v2/project/${projectID}/user/${simpleID}/messages/${messageIndex}/refresh`
  );
  return response.data;
}

// ========== Delete & Reschedule Endpoints ==========

export interface DeleteScheduledMessageResponse {
  success: boolean;
  message: string;
}

export interface RescheduleMessageRequest {
  scheduledAt: string; // ISO 8601 timestamp
  timeZone?: string; // Optional timezone override
}

export interface RescheduleMessageResponse {
  success: boolean;
  message: string;
  previousScheduledAt: string;
  newScheduledAt: string;
}

/**
 * Delete a scheduled message that hasn't been sent yet
 */
export async function deleteScheduledMessage(
  projectID: string,
  messageID: string
): Promise<DeleteScheduledMessageResponse> {
  try {
    const response = await api.delete<DeleteScheduledMessageResponse>(
      `/v2/project/${projectID}/messages/${messageID}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.error ||
        error.message ||
        'Failed to delete scheduled message'
    );
  }
}

/**
 * Reschedule a pending message to a new time
 */
export async function rescheduleMessage(
  projectID: string,
  messageID: string,
  request: RescheduleMessageRequest
): Promise<RescheduleMessageResponse> {
  try {
    const response = await api.patch<RescheduleMessageResponse>(
      `/v2/project/${projectID}/messages/${messageID}/schedule`,
      request
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.error ||
        error.message ||
        'Failed to reschedule message'
    );
  }
}

// ========== Dev/Debug Endpoints ==========

export interface ResetDebounceResponse {
  success: boolean;
  message: string;
  oldLastConsideredTime: string;
  newLastConsideredTime: string;
  messageEditsToday: number;
  lastMessageEditTime: string;
}

export interface TriggerIdeaUpdateResponse {
  success: boolean;
  message: string;
  messagesEdited: number;
  messagesSkipped: number;
  details?: Array<{
    messageId: string;
    originalIdea: string;
    updatedIdea: string;
    reason: string;
  }>;
}

export interface ResetEditLimitResponse {
  success: boolean;
  message: string;
  oldEditsToday: number;
  oldLastEditTime: string;
  newEditsToday: number;
  newLastEditTime: string;
}

/**
 * [DEV] Reset the debounce timer for session summarization
 * This allows the next pixel event to trigger immediate AI summarization
 */
export async function devResetDebounce(
  projectID: string,
  simpleID: string
): Promise<ResetDebounceResponse> {
  const response = await api.post<ResetDebounceResponse>(
    `/v2/project/${projectID}/user/${simpleID}/dev/reset-debounce`
  );
  return response.data;
}

/**
 * [DEV] Manually trigger scheduled message idea updates based on recent session summaries
 */
export async function devTriggerIdeaUpdate(
  projectID: string,
  simpleID: string
): Promise<TriggerIdeaUpdateResponse> {
  const response = await api.post<TriggerIdeaUpdateResponse>(
    `/v2/project/${projectID}/user/${simpleID}/dev/trigger-idea-update`
  );
  return response.data;
}

/**
 * [DEV] Reset the daily message edit limit for a user
 */
export async function devResetEditLimit(
  projectID: string,
  simpleID: string
): Promise<ResetEditLimitResponse> {
  const response = await api.post<ResetEditLimitResponse>(
    `/v2/project/${projectID}/user/${simpleID}/dev/reset-edit-limit`
  );
  return response.data;
}

/**
 * Simulate an AI-generated customer reply in demo mode
 */
export interface DemoSimulateReplyRequest {
  customerId: string;
  message: string;
  medium: 'sms' | 'email';
}

export interface DemoSimulateReplyResponse {
  reply: string;
  customerName: string;
  messageId: string;
  medium: string;
}

export async function simulateDemoReply(
  projectID: string,
  request: DemoSimulateReplyRequest
): Promise<DemoSimulateReplyResponse> {
  const response = await api.post<{ Data: DemoSimulateReplyResponse }>(
    `/v2/project/${projectID}/demo/simulate-reply`,
    request
  );
  return response.data.Data;
}

// ========== Walkthrough AI (SSE) ==========

export interface WalkthroughAISummary {
  sessionId: string;
  summary: string;
  eventCount: number;
  startTime: string;
  endTime: string;
}

export interface WalkthroughAIMessageChange {
  id: string;
  previousContent: string;
  newContent: string;
  reason: string;
  scheduledAt: string;
  title: string;
  medium: string;
  isNew: boolean;
}

export interface WalkthroughAIResults {
  summaries: WalkthroughAISummary[] | null;
  previousInsight: string | null;
  newInsight: string | null;
  messages: WalkthroughAIMessageChange[] | null;
}

/**
 * Call the combined walkthrough AI endpoint via SSE (POST with streaming).
 * Runs session summary, insight, and message editing in parallel on the backend.
 * Calls the provided callbacks as each result streams in.
 */
export async function streamWalkthroughAI(
  projectID: string,
  customerId: string,
  callbacks: {
    onSummary?: (summaries: WalkthroughAISummary[]) => void;
    onInsight?: (previousInsight: string, newInsight: string) => void;
    onMessage?: (messages: WalkthroughAIMessageChange[]) => void;
    onError?: (task: string, error: string) => void;
    onDone?: (previousInsight: string) => void;
  }
): Promise<WalkthroughAIResults> {
  const { API_BASE } = await import('./apiBase');
  const url = `${API_BASE}/v2/project/${projectID}/demo/walkthrough-ai`;

  // Get auth token (demo or Firebase)
  const { useDemoStore } = await import('@/store/demoStore');
  const demoStore = useDemoStore();
  let authToken = '';
  if (demoStore.isDemo && demoStore.demoAccessToken) {
    authToken = demoStore.demoAccessToken;
  } else {
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    authToken = (await auth.currentUser?.getIdToken()) || '';
  }

  const results: WalkthroughAIResults = {
    summaries: null,
    previousInsight: null,
    newInsight: null,
    messages: null
  };

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`
    },
    body: JSON.stringify({ customerId })
  });

  if (!response.ok) {
    throw new Error(`Walkthrough AI request failed: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response body reader');

  const decoder = new TextDecoder();
  let buffer = '';
  let streaming = true;

  while (streaming) {
    const { done, value } = await reader.read();
    if (done) {
      streaming = false;
      continue;
    }

    buffer += decoder.decode(value, { stream: true });

    // Parse SSE events from buffer
    const lines = buffer.split('\n');
    buffer = lines.pop() || ''; // Keep incomplete line in buffer

    let currentEvent = '';
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEvent = line.substring(7).trim();
      } else if (line.startsWith('data: ') && currentEvent) {
        const dataStr = line.substring(6);
        try {
          const data = JSON.parse(dataStr);

          switch (currentEvent) {
            case 'session-summary':
              results.summaries = data.summaries;
              callbacks.onSummary?.(data.summaries);
              break;
            case 'insight':
              results.previousInsight = data.previousInsight;
              results.newInsight = data.newInsight;
              callbacks.onInsight?.(data.previousInsight, data.newInsight);
              break;
            case 'message':
              results.messages = data.messages;
              callbacks.onMessage?.(data.messages);
              break;
            case 'error':
              callbacks.onError?.(data.task, data.error);
              break;
            case 'done':
              results.previousInsight =
                results.previousInsight ?? data.previousInsight;
              callbacks.onDone?.(data.previousInsight);
              break;
          }
        } catch {
          console.warn('[walkthrough-ai] Failed to parse SSE data:', dataStr);
        }
        currentEvent = '';
      } else if (line === '') {
        currentEvent = '';
      }
    }
  }

  return results;
}
